<template>
  <main class="mb-5">
    <header>
      <button
        class="flex items-center h-8 gap-3 px-0 mb-6 xl btn-simple"
        @click="backToListDenounces()"
      >
        <v-icon name="co-arrow-left" width="24" height="24" />
        <span v-if="isCommentDenounces()">
          {{
            $t("forum.listDenounces.tableDenounces.buttons.toComments")
          }}
        </span>
        <span v-else>
          {{ $t("forum.listDenounces.tableDenounces.buttons.toTopics") }}
        </span>
      </button>
      <div class="flex items-center justify-between mb-6">
        <h6
          v-if="isCommentDenounces()"
          class="text-[18px] gap-3 text-h-neutral-900"
        >
          {{
            $t("labels.generic", {
              pt: "Autor do comentário: ",
              en: "Comment author: ",
            })
          }}
          <span class="font-normal">{{
            denounceComments?.comment?.userAuthor?.name
          }}</span>
        </h6>
        <h6 v-else class="font-bold text-[18px] gap-3 flex text-h-neutral-900">
          {{
            $t("labels.generic", {
              pt: "Título do tópico: ",
              en: "Topic title: ",
            })
          }}
          <span class="font-normal max-w-[30vw] truncate">{{
            denounceTopic?.topic ? denounceTopic?.topic.title : ""
            }}</span>
        </h6>
        <div class="flex h-8 gap-4">
          <Button v-if="selectedDenounces.length > 0" class="gap-2 btn-delete-block !p-2"
            @click="showDialogDeclineDenounce()">
            <v-icon name="md-cancel-outlined" scale="0.8" />
            <p class="px-0 text-base">
              {{
                $t("labels.generic", { pt: "Dispensar", en: "Decline" })
              }}
            </p>
          </Button>
          <Button v-if="selectedDenounces.length > 0" class="gap-2 btn-primary !p-2"
            @click="showDialogAcceptDenounce()">
            <v-icon name="md-taskalt-outlined" scale="0.8" />
            <p class="px-0 text-base">
              {{
                $t("labels.generic", { pt: "Aceitar", en: "Accept" })
              }}
            </p>
          </Button>
          <Button class="flex gap-3 items-center btn-outline !px-2" @click="sendDisplayedItem()">
            <i class="pi pi-search" />
            <p v-if="isCommentDenounces()" class="px-0 text-base">
              {{
                $t("labels.generic", { pt: "Ver comentário", en: "See comment" })
              }}
            </p>
            <p v-else>
              {{ $t("labels.generic", { pt: "Ver tópico", en: "See topic" }) }}
            </p>
          </Button>
        </div>
      </div>
    </header>
    <section>
      <DataTable
        :value="denounceComments?.denounces ?? denounceTopic?.denounces"
        :paginator="totalOfDenounces() > 10"
        :rows="size"
        @page="onPaginate"
        :first="firstIndexToPaginate"
        showGridlines
        class="custom-datatableForum !shadow-md rounded-md mb-16"
        v-model:selection="selectedDenounces"
      >
        <template #paginatorstart>
          <p class="text-xs">{{ formatePaginatorText() }}</p>
        </template>
        <Column 
          class="table-style" 
          field="id" 
          headerClass="w-[72px] !bg-h-primary-500" 
          selectionMode="multiple"
        />
        <Column
          class="table-style"
          field="created"
          :header="
            $t('forum.listDenounces.tableDenounces.columns.dateOfDenounce')
          "
          headerClass="!bg-h-primary-500 w-[200px]"
        >
          <template #body="{ data }">
            <p>{{ formatDate(data.created as string) }}</p>
          </template>
        </Column>
        <Column
          class="table-style max-w-[492px] truncate"
          field="userAuthor"
          :header="
            $t('forum.listDenounces.tableDenounces.columns.denounceAuthor')
          "
          headerClass="!bg-h-primary-500 w-[492px]"
        >
        </Column>
        <Column
          class="table-style"
          field="description"
          :header="$t('forum.listDenounces.tableDenounces.columns.description')"
          headerClass="!bg-h-primary-500 w-[492px]"
        >
        </Column>
        <Column
          class="table-style"
          field="status"
          :header="$t('forum.listDenounces.tableDenounces.columns.status')"
          headerClass="!bg-h-primary-500 w-48"
        >
          <template #body="{ data }">
            <p
              class="px-1 text-sm rounded-2xl"
              :class="labelDenouncesStyle[data.status]"
            >
              {{ getStatusText(data.status) }}
            </p>
          </template>
        </Column>
        <Column 
          class="table-style" 
          :header="$t('forum.listDenounces.tableDenounces.columns.actions')"
          headerClass="!bg-h-primary-500 w-[126px]"
        >
          <template #body="{ data }">
            <span class="flex justify-center gap-7">
              <button @click="showDialogDeclineDenounce(data)" v-tooltip.top="$t('labels.generic', {
                pt: 'Recusar denúncia',
                en: 'Decline denounce',
              })
                ">
                <v-icon name="md-cancel-outlined" class="text-h-error-400" width="20" height="20" />
              </button>
              <button @click="showDialogAcceptDenounce(data)" v-tooltip.top="$t('labels.generic', {
                pt: 'Aceitar denúncia',
                en: 'Accept denounce',
              })
                ">
                <v-icon name="md-taskalt-outlined" class="text-h-primary-500" width="20" height="20" />
              </button>
            </span>
          </template>
        </Column>
      </DataTable>
    </section>
    <ConfirmDialog group="denouncing" :style="{ maxWidth: '490px' }" class="w-11/12 custom-confirm-dialog" />
  </main>
</template>
<script lang="ts">
import {
  Denounce,
  DenounceStatus,
  DenounceTopic,
  DenouncedComment,
} from "@/models/denounce.model";
import { defineComponent, PropType } from "vue";
import moment from "moment";
import { PageState } from "primevue/paginator";
import { ToastFactory } from "@/utils/toast-factory.util";
import { Messages } from "@/utils/messages.util";
import { ForumService } from "../../forum.service";
import { take } from "rxjs";

export default defineComponent({
  name: "table-denounces",
  data() {
    return {
      firstIndexToPaginate: 0,
      numberOfDenouncesPerPage: 10,
      size: 10,
      labelDenouncesStyle: {
        ACCEPTED: "bg-h-success-200 text-h-success-500",
        NOT_ACCEPTED: "bg-h-neutral-200 text-h-neutral-700",
        WAITING_REVIEW: "bg-h-warning-100 text-h-warning-700",
      },
      selectedDenounces: [],
    };
  },
  props: {
    denounceComments: {
      type: Object as PropType<DenouncedComment>,
      required: false,
      default: new DenouncedComment(),
    },
    denounceTopic: {
      type: Object as PropType<DenounceTopic>,
      required: false,
      default: new DenounceTopic(),
    },
    denounceType: {
      type: String,
    },
  },
  methods: {
    sendDisplayedItem(): void {
      let displayedItem: DenounceTopic | DenouncedComment = this.setDisplayedItem();
      this.$emit("displayedItem", displayedItem);
    },
    setDisplayedItem(): DenounceTopic | DenouncedComment {
      return this.isCommentDenounces() ? this.denounceComments : this.denounceTopic;
    },
    backToListDenounces(): void {
      this.numberOfDenouncesPerPage = 10;
      this.firstIndexToPaginate = 0;
      this.$emit("closeTableDenounces");
      this.selectedDenounces = [];
    },
    formatDate(date: string): string {
      let result: any = date.split(" ");
      result = moment
        .utc(result[0])
        .local(true)
        .format(
          `DD/MM/YYYY [${this.$t("labels.generic", {
            pt: "às",
            en: "at",
          })}] HH:mm`
        );
      return result;
    },
    getStatusText(status: string): string | undefined {
      switch (status) {
        case DenounceStatus.ACCEPTED:
          return this.$t("labels.generic", { pt: "Aceito", en: "Accepted" });
        case DenounceStatus.NOT_ACCEPTED:
          return this.$t("labels.generic", {
            pt: "Não aceito",
            en: "Not accepted",
          });
        case DenounceStatus.WAITING_REVIEW:
          return this.$t("labels.generic", {
            pt: "Esperando revisão",
            en: "Waiting for review",
          });
      }
    },
    isCommentDenounces(): boolean {
      return (
        this.denounceType ===
        this.$t("labels.generic", { pt: "Comentários", en: "Comments" })
      );
    },
    isTopicDenounces(): boolean {
      return (
        this.denounceType ===
        this.$t("labels.generic", { pt: "Tópicos", en: "Topic" })
      );
    },
    formatePaginatorText(): string {
      return this.$t("forum.listDenounces.tableDenounces.paginatorText", {
        first: this.firstIndexToPaginate + 1,
        last: this.numberOfDenouncesPerPage,
        total: this.totalOfDenounces(),
      });
    },
    totalOfDenounces(): number {
      let total = 0;
      if (this.denounceComments.denounces) {
        total = this.denounceComments.denounces?.length as number;
      } else if (this.denounceTopic.denounces) {
        total = this.denounceTopic.denounces?.length as number;
      }
      return total;
    },
    onPaginate(event: PageState): void {
      if (event.pageCount === event.page + 1 && event.pageCount > 1) {
        this.numberOfDenouncesPerPage =
          event.first + (this.totalOfDenounces() % event.rows);
      } else {
        this.numberOfDenouncesPerPage = event.first + event.rows;
      }
      this.firstIndexToPaginate = event.first;
    },
    setSelectedDenouncesStatus(denounces: Array<Denounce>, isAccepted: boolean): Array<Denounce> {
      return denounces.map((denounce) => {
        denounce.status = isAccepted ? DenounceStatus.ACCEPTED : DenounceStatus.NOT_ACCEPTED
        return denounce
      });
    },
    editCommentDenounce(isAccepted: boolean, denounce?: Denounce): void {
      this.service.denounce.pipe(take(1)).subscribe({
        next: () => {
          if (isAccepted)
            this.$toast.add(ToastFactory.success(Messages.FORUM_DENOUNCE_ACCEPTED));
          else
            this.$toast.add(ToastFactory.success(Messages.FORUM_DENOUNCE_DISMISSED));
          this.$emit("updateDenounces");
        }
      });
      if (denounce) {
        this.service.editCommentDenounce(this.denounceComments?.comment!.id, this.setSelectedDenouncesStatus([denounce], isAccepted));
      }
      else {
        this.service.editCommentDenounce(this.denounceComments?.comment!.id, this.setSelectedDenouncesStatus(this.selectedDenounces, isAccepted));
        this.selectedDenounces = [];
      }
    },
    editTopicDenounce(isAccepted: boolean, denounce?: Denounce): void {
      this.service.denounce.pipe(take(1)).subscribe({
        next: () => {
          if (isAccepted)
            this.$toast.add(ToastFactory.success(Messages.FORUM_DENOUNCE_ACCEPTED));
          else
            this.$toast.add(ToastFactory.success(Messages.FORUM_DENOUNCE_DISMISSED));
          this.$emit("updateDenounces");
        }
      });
      if (denounce) {
        this.service.editTopicDenounce(this.denounceTopic.topic?.id!, this.setSelectedDenouncesStatus([denounce], isAccepted));
      }
      else {
        this.service.editTopicDenounce(this.denounceTopic.topic?.id!, this.setSelectedDenouncesStatus(this.selectedDenounces, isAccepted));
        this.selectedDenounces = [];
      }
    },
    showDialogAcceptDenounce(denounce?: Denounce): void {
      this.$confirm.require({
        group: "denouncing",
        header: (denounce) ? this.$t("forum.listDenounces.dialog.accept.header") : this.$t("forum.listDenounces.dialog.accept.headerMultiple", { selectedDenounces: this.selectedDenounces.length }),
        message: this.modalAcceptanceMessage(),
        rejectLabel: this.$t("labels.generic", {
          pt: "Cancelar",
          en: "Cancel"
        }),
        acceptLabel: this.$t("labels.generic", {
          pt: "Aceitar denúncia(s)",
          en: "Accept report(s)",
        }),
        acceptClass: "btn-primary",
        rejectClass: "btn-outline",
        accept: () => {
          if (this.isCommentDenounces()) {
            this.editCommentDenounce(true, denounce);
            return;
          }
          if (this.isTopicDenounces()) {
            this.editTopicDenounce(true, denounce);
            return;
          }
        }
      })
    },
    showDialogDeclineDenounce(denounce?: Denounce): void {
      this.$confirm.require({
        group: "denouncing",
        header: (denounce) ? this.$t("forum.listDenounces.dialog.decline.header") : this.$t("forum.listDenounces.dialog.decline.headerMultiple", { selectedDenounces: this.selectedDenounces.length }),
        message: this.modalDeclineMessage(),
        rejectLabel: this.$t("labels.generic", {
          pt: "Cancelar",
          en: "Cancel"
        }),
        acceptLabel: this.$t("labels.generic", {
          pt: "Dispensar denúncia(s)",
          en: "Dismiss report(s)",
        }),
        acceptClass: "btn-delete-block",
        rejectClass: "btn-outline",
        accept: () => {
          if (this.isCommentDenounces()) {
            this.editCommentDenounce(false, denounce);
            return;
          }

          if (this.isTopicDenounces()) {
            this.editTopicDenounce(false, denounce);
            return;
          }
        }
      })
    },
    modalAcceptanceMessage(): string {
      if (this.isCommentDenounces())
        return this.$t("forum.listDenounces.dialog.accept.comment");
      else
        return this.$t("forum.listDenounces.dialog.accept.topic");
    },
    modalDeclineMessage(): string {
      if (this.isCommentDenounces())
        return this.$t("forum.listDenounces.dialog.decline.comment");
      else
        return this.$t("forum.listDenounces.dialog.decline.topic");
    }
  },
  computed: {
    service(): ForumService {
      return new ForumService();
    },
  },
});
</script>
